import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

type SkeletonVariant = 'text' | 'block' | 'circle';

/**
 * A loading placeholder. Purely visual: it never carries a status announcement
 * of its own.
 *
 * - `aria-hidden` always. What announces the load is the container with
 *   `aria-busy` (`DataTable` at `isLoading`, say), not each placeholder: if each
 *   one announced itself, a screen reader would say "loading" fifteen times in a
 *   table with fifteen skeleton rows.
 * - `motion-reduce:animate-none` turns the pulse off under
 *   `prefers-reduced-motion`. Without the animation the element does not become
 *   invisible: it returns to its normal opacity, that is, to the fixed neutral
 *   block the contract asks for.
 */
const skeletonVariants = cva('bg-subtle animate-pulse motion-reduce:animate-none', {
  variants: {
    variant: {
      text: 'rounded-sm',
      block: 'rounded-md',
      circle: 'rounded-full',
    } satisfies Record<SkeletonVariant, string>,
  },
  defaultVariants: {
    variant: 'text',
  },
});

export interface SkeletonProps {
  /** The placeholder's shape: `text` respects the typographic role's line height. */
  variant?: SkeletonVariant;
  /**
   * Number of lines. It only makes sense with `variant='text'`: with
   * `block`/`circle` the placeholder is a single shape, so `lines` is ignored
   * silently (there are no "lines" to draw in a rectangle or a circle).
   */
  lines?: number;
  /**
   * A Tailwind class for the placeholder's width (`'w-40'`, `'w-1/2'`), not a raw
   * CSS value — the component accepts no `style`. It is the only door to a
   * literal in this whole file: the caller can write a Tailwind arbitrary value
   * here (`'w-[24px]'`) and it would compile, but that is the consumer's
   * decision, not this component's, which never picks a size on its own.
   */
  width?: string;
  /** The same as `width`, for the height. */
  height?: string;
  className?: string;
}

function SkeletonShape({
  variant,
  sizeClassName,
  width,
  height,
  className,
}: {
  variant: SkeletonVariant;
  sizeClassName: string;
  width?: string;
  height?: string;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      aria-hidden="true"
      className={cn(skeletonVariants({ variant }), sizeClassName, width, height, className)}
    />
  );
}

export function Skeleton({
  variant = 'text',
  lines = 1,
  width,
  height,
  className,
}: SkeletonProps): React.JSX.Element {
  if (variant === 'text') {
    // `h-[1lh]` ties the height to the `lh` unit (the element's own resolved
    // line-height): placed beside `text-body` on the same node, the placeholder
    // measures exactly that typographic role's line height, never a
    // hand-invented value. If it did not match, the layout would jump when the
    // real text arrived — which is precisely what this component exists to
    // avoid.
    const lineCount = Math.max(1, Math.trunc(lines));

    if (lineCount === 1) {
      return (
        <SkeletonShape
          variant={variant}
          sizeClassName="text-body h-[1lh] w-full"
          width={width}
          height={height}
          className={className}
        />
      );
    }

    return (
      <div aria-hidden="true" className={cn('flex flex-col gap-stack-sm', className)}>
        {Array.from({ length: lineCount }, (_, index) => (
          <SkeletonShape
            // The index is a stable key here: the placeholders have no identity
            // of their own and the list never reorders or filters.
            key={index}
            variant={variant}
            sizeClassName="text-body h-[1lh] w-full"
            width={width}
            height={height}
          />
        ))}
      </div>
    );
  }

  return (
    <SkeletonShape
      variant={variant}
      sizeClassName={variant === 'circle' ? 'size-icon-md' : 'h-row w-full'}
      width={width}
      height={height}
      className={className}
    />
  );
}
